/**
 * UranIA PA — `<CitazioniSidebar />` (Fase 2 US-026 AC#1).
 *
 * Pannello laterale che renderizza l'elenco delle fonti citate
 * (`[FONTE: ...]`) restituite dal Gateway (`GatewayResponse.sources`) per
 * la pagina chat / workspace Mod5.  AgID P.7 richiede che ogni risposta
 * basata su RAG esponga sempre la lista delle fonti consultate: questo
 * componente e' il punto di rendering single-source-of-truth in SPA.
 *
 * Prop contract (AC#2 letterale "Receives prop sources: [{doc_id,
 * section, page, score}]") --- la shape combacia con
 * `RetrievedChunkResponse` di `services/rag/openapi.yaml` (US-014 AC#5):
 *   - `doc_id`: slug stabile del documento sorgente.
 *   - `section`: heading H1/H2 di appartenenza.
 *   - `page`: pagina 1-based del primo carattere (`null` se non
 *     applicabile, es. testi senza pagine come SORI).
 *   - `score`: score blendato `alpha*norm(bm25) + beta*norm(dense) +
 *     gamma*rerank` (range 0.0-1.0).
 *
 * Behavior:
 *   - Empty state (AC#5 letterale): se `sources` e' vuoto mostra un
 *     placeholder testuale con `data-testid` dedicato.  Il caso
 *     coincide con "il Gateway non ha consumato il RAG" o
 *     "ho consumato il RAG ma nessun chunk e' rilevante" --- in
 *     entrambi i casi l'utente deve vedere esplicitamente che la
 *     risposta NON ha fonti.
 *   - Click su un item (AC#4 letterale "Click item -> opens
 *     placeholder document viewer"): toggle inline di un pannello
 *     viewer ancora placeholder.  In Fase 4 (US-079+) il pannello
 *     sara' sostituito dal renderer nativo del chunk PDF.  La logica
 *     placeholder vive interamente client-side: non ci sono fetch
 *     verso il backend (no rischi di esposizione cross-tenant).
 *   - Rendering deterministico: l'ordine dell'array di input e'
 *     preservato (l'API RAG ritorna gia' i chunk in ordine di score
 *     decrescente, US-014 AC#3).
 *
 * A11y:
 *   - Container `<aside>` con `aria-label` localizzato.
 *   - Lista come `<ul>`/`<li>` semantici.
 *   - Item come `<button type="button">` per essere keyboard-
 *     reachable e annunciato come trigger dal screen reader.
 *   - Stato selezione comunicato via `aria-expanded` sul button e
 *     `aria-controls` sul pannello viewer (WAI-ARIA AP §3.6 Disclosure).
 *   - Viewer panel `role="region"` con `aria-labelledby` puntato al
 *     button che lo ha aperto.
 *
 * Boundary di sicurezza:
 *   - Niente PII nei `data-testid`: solo `doc_id` (slug pubblico).
 *   - Il componente NON ricostruisce URL del documento: il viewer
 *     reale (Fase 4) prendera' presigned URL S3 versionati dal BC
 *     RAG; qui solo placeholder testuale.
 */

import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

/**
 * Shape di una singola citazione esposta dal Gateway (AC#2 letterale).
 * Allineato a `RetrievedChunkResponse` (services/rag/openapi.yaml).
 */
export interface CitazioneSource {
  readonly doc_id: string;
  readonly section: string;
  readonly page: number | null;
  readonly score: number;
}

export interface CitazioniSidebarProps {
  /**
   * Elenco fonti citate. Vuoto -> empty state (AC#5).  L'ordine viene
   * preservato cosi' com'e' (il backend lo restituisce gia' score-
   * sorted desc).
   */
  readonly sources: readonly CitazioneSource[];
}

const PERCENT_DIGITS = 1;

function formatScore(score: number): string {
  // Score blendato 0.0-1.0 -> percentuale con 1 decimale (es. 87.3%).
  // `Intl.NumberFormat` it-IT mette la virgola decimale, consistent con
  // la lingua dell'app (i18n.fallbackLng=it).
  return new Intl.NumberFormat('it-IT', {
    style: 'percent',
    minimumFractionDigits: PERCENT_DIGITS,
    maximumFractionDigits: PERCENT_DIGITS,
  }).format(score);
}

export function CitazioniSidebar(props: CitazioniSidebarProps): JSX.Element {
  const { t } = useTranslation();
  const { sources } = props;
  const baseId = useId();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Memoize per evitare ri-render dei children quando `sources` non e' cambiato.
  const items = useMemo(() => sources, [sources]);

  const handleToggle = useCallback((index: number): void => {
    setSelectedIndex((current) => (current === index ? null : index));
  }, []);

  const isEmpty = items.length === 0;
  const selectedSource =
    selectedIndex !== null && selectedIndex < items.length ? items[selectedIndex] : undefined;

  return (
    <aside
      className="urania-citazioni-sidebar card"
      data-testid="citazioni-sidebar"
      aria-label={t('citazioni_sidebar.aria_label')}
    >
      <div className="card-header">
        <h2 className="h6 mb-0">{t('citazioni_sidebar.title')}</h2>
      </div>
      <div className="card-body">
        {isEmpty ? (
          <p className="text-muted small mb-0" data-testid="citazioni-sidebar-empty">
            {t('citazioni_sidebar.empty')}
          </p>
        ) : (
          <ul className="list-unstyled mb-0" data-testid="citazioni-sidebar-list">
            {items.map((source, index) => {
              const isSelected = index === selectedIndex;
              const itemId = `${baseId}-item-${String(index)}`;
              const viewerId = `${baseId}-viewer-${String(index)}`;
              const pageLabel =
                source.page !== null
                  ? t('citazioni_sidebar.item.page', { page: source.page })
                  : t('citazioni_sidebar.item.page_unknown');
              return (
                <li
                  key={`${source.doc_id}-${String(index)}`}
                  className="urania-citazioni-sidebar__item border-bottom py-2"
                  data-testid={`citazioni-sidebar-item-${String(index)}`}
                >
                  <button
                    type="button"
                    id={itemId}
                    className="btn btn-link p-0 text-start w-100 d-block"
                    aria-expanded={isSelected}
                    aria-controls={viewerId}
                    onClick={(): void => {
                      handleToggle(index);
                    }}
                    data-testid={`citazioni-sidebar-item-button-${String(index)}`}
                  >
                    <span
                      className="d-block fw-bold"
                      data-testid={`citazioni-sidebar-item-doc-${String(index)}`}
                    >
                      {source.doc_id}
                    </span>
                    <span
                      className="d-block small text-muted"
                      data-testid={`citazioni-sidebar-item-section-${String(index)}`}
                    >
                      {source.section}
                    </span>
                    <span className="d-block small">
                      <span data-testid={`citazioni-sidebar-item-page-${String(index)}`}>
                        {pageLabel}
                      </span>
                      <span className="text-muted">{' · '}</span>
                      <span
                        className="badge text-bg-secondary"
                        data-testid={`citazioni-sidebar-item-score-${String(index)}`}
                      >
                        {formatScore(source.score)}
                      </span>
                    </span>
                  </button>
                  {isSelected && selectedSource !== undefined && (
                    <div
                      id={viewerId}
                      role="region"
                      aria-labelledby={itemId}
                      className="urania-citazioni-sidebar__viewer mt-2 p-2 bg-light rounded small"
                      data-testid={`citazioni-sidebar-viewer-${String(index)}`}
                    >
                      <p className="mb-1 fw-semibold">{t('citazioni_sidebar.viewer.title')}</p>
                      <p className="mb-0 text-muted">
                        {t('citazioni_sidebar.viewer.body', {
                          doc_id: selectedSource.doc_id,
                          section: selectedSource.section,
                          page: selectedSource.page ?? '—',
                        })}
                      </p>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
